import {
    IPhraseTriggerResult,
    IPhraseTriggerResultParameters,
    IPhraseTriggerResultsResponse,
    Route,
} from "@edgetier/watchtower-types";
import { requestInteractionFromMessageId } from "hooks-for/interactions/use-interaction-message";
import { useMemo } from "react";
import { useQueries } from "react-query";
import { axios } from "utilities/axios";

/**
 * Request phrase trigger results with pagination.
 * @param url              Phrase trigger URL.
 * @param parameters
 * @param nextPage         Optional URL of the next page to load.
 * @returns                Promise of an array of phrase trigger results.
 */
export const requestPhraseTriggerResults = async (
    url: string,
    parameters: IPhraseTriggerResultParameters,
    nextPage?: string
) => {
    // If loading the next page, the backend provides a full URL. Otherwise construct a search query.
    const request = nextPage
        ? axios.get<IPhraseTriggerResultsResponse>(nextPage)
        : axios.get<IPhraseTriggerResultsResponse>(url, { params: parameters });

    const response = await request;
    return response.data;
};

/**
 * Generate a gradient for the similarity bar. By playing with the stop percentages, we can make the gradient show
 * three solid colours: blue for the threshold, green for the similarity percentage and grey for what's remaining
 * https://stackoverflow.com/questions/45097591/generate-solid-colors-using-css-linear-gradient-not-smooth-colors
 */
export const generateSimilarityGradient = (similarityThreshold: number, similarityPercentage: number) => {
    return `linear-gradient(
        to right,
        #3498db,
        #3498db ${similarityThreshold}%,
        #16a085 ${similarityThreshold}%,
        #16a085 ${similarityPercentage}%,
        #ecf0f1 ${similarityPercentage}%
    )`;
};

/**
 * Fill the languageIsoCode into phrase trigger results
 * @param phraseTriggerResults
 * @param phraseTriggerResultsLanguages
 */
export const setLanguageToPhraseTriggerResult = (
    phraseTriggerResults: IPhraseTriggerResult[],
    phraseTriggerResultsLanguages: Record<string, string>
) => {
    if (phraseTriggerResults.length === 0) return [];

    return phraseTriggerResults.map((phraseTriggerResult) => ({
        ...phraseTriggerResult,
        languageIsoCode: phraseTriggerResultsLanguages[phraseTriggerResult.interactionMessageId],
    }));
};

/**
 * Hook to request phrase trigger results languages based on their interactionMessageId
 * @param phraseTriggerResults
 */
export const usePhraseTriggerResultsLanguages = (phraseTriggerResults: IPhraseTriggerResult[]) => {
    // Query to retrieve all interaction messages from phrase trigger results
    const phraseTriggerResultsMessages = useQueries(
        phraseTriggerResults.map((phraseTriggerResult) => {
            const url = Route.InteractionsMessage.replace(
                ":interactionMessageId",
                phraseTriggerResult.interactionMessageId
            );
            return {
                queryKey: [url, phraseTriggerResult.interactionMessageId],
                queryFn: () => requestInteractionFromMessageId(url),
            };
        })
    );

    const isPhraseTriggerResultsLanguagesLoading = useMemo(
        () => phraseTriggerResultsMessages.some(({ isLoading }) => isLoading),
        [phraseTriggerResultsMessages]
    );

    // Select only the language iso code from interaction message details.
    const phraseTriggerResultsLanguages: Record<string, string> = useMemo(
        () =>
            (phraseTriggerResultsMessages ?? [])
                .flatMap(({ data }) => data)
                .reduce((previousValues, currentValue) => {
                    return typeof currentValue !== "undefined"
                        ? {
                              ...previousValues,
                              [currentValue.interactionMessageId]: currentValue.languageIsoCode.toLocaleLowerCase(),
                          }
                        : { ...previousValues };
                }, {}),
        [phraseTriggerResultsMessages]
    );

    return {
        phraseTriggerResultsLanguages,
        isPhraseTriggerResultsLanguagesLoading,
    };
};
